
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Lock } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ChangePasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const ChangePasswordForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const form = useForm<ChangePasswordFormValues>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const handleSubmit = async (values: ChangePasswordFormValues) => {
    if (values.newPassword !== values.confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    if (values.newPassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword
      });

      if (error) {
        console.error("Error updating password:", error);
        toast.error("Error al cambiar la contraseña");
        return;
      }

      toast.success("Contraseña actualizada exitosamente");
      setLastUpdated(new Date());
      form.reset();
    } catch (error) {
      console.error("Error updating password:", error);
      toast.error("Error al cambiar la contraseña");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="currentPassword"
          rules={{ required: "La contraseña actual es requerida" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base font-semibold text-navy">Contraseña Actual</FormLabel>
              <FormControl>
                <Input 
                  type="password"
                  placeholder="Ingresa tu contraseña actual" 
                  className="min-h-[44px] text-base px-5 py-4 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  autoComplete="current-password"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="newPassword"
          rules={{ 
            required: "La nueva contraseña es requerida",
            minLength: {
              value: 6,
              message: "La contraseña debe tener al menos 6 caracteres"
            }
          }}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base font-semibold text-navy">Nueva Contraseña</FormLabel>
              <FormControl>
                <Input 
                  type="password"
                  placeholder="Ingresa tu nueva contraseña" 
                  className="min-h-[44px] text-base px-5 py-4 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  autoComplete="new-password"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmPassword"
          rules={{ required: "Confirma tu nueva contraseña" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base font-semibold text-navy">Confirmar Nueva Contraseña</FormLabel>
              <FormControl>
                <Input 
                  type="password"
                  placeholder="Confirma tu nueva contraseña" 
                  className="min-h-[44px] text-base px-5 py-4 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  autoComplete="new-password"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="pt-6">
          <Button 
            type="submit" 
            disabled={isLoading}
            className="w-full sm:w-auto min-h-[44px] px-8 bg-primary hover:bg-primary/90 hover:shadow-md focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {!isLoading && <Lock className="mr-2 h-4 w-4" />}
            Cambiar Contraseña
          </Button>
          
          {lastUpdated && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-medium text-green-800">
                ✅ Contraseña actualizada exitosamente
              </p>
              <p className="text-xs text-green-600 mt-1">
                Última actualización: {lastUpdated.toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          )}
        </div>
      </form>
    </Form>
  );
};
